/**
 * Eine einzelne **Erzeugungsanlage** (HM-131) —
 * `/anlagen/{id}/erzeugungsanlagen/{assetId}?zeitraum=`.
 *
 * **Die Reihenfolge ist die der Fragen**, wie am Zähler: was leistet sie
 * gerade (Kacheln), wie hat sie sich verhalten (Verlauf), woraus besteht sie
 * (Geräte), was steht fest (Stammdaten), wie geht sie in die Bilanz ein
 * (Zähler), roter Kasten. Name, Art und Kennzahl stehen in der
 * Kopfleiste des Rahmens (`generationAssetHeadline`), nicht noch einmal hier.
 *
 * **Werte kommen aus ihren Geräten, nicht aus dem Messkonzept.** Kacheln und
 * Verlauf lesen die Rohschicht der Wechselrichter, Laderegler und Batterien
 * (`GET /generation-assets/{id}/state` und `/history`) — die Frage ist „was
 * haben die Geräte dieser Anlage gemeldet", nicht „was hat das Haus erzeugt".
 * Die Bilanz bleibt bei „Strom & Erzeugung". Deshalb steht der Ertrag je kWp
 * hier für **diese** Anlage und dort für alle Erzeugungszähler zusammen.
 *
 * **Gerechnet wird in der API**: Summe über die Geräte, Ertrag im Fenster,
 * Ertrag je kWp. Fehlt einem Gerät ein Wert, ist die Summe unbekannt und das
 * Diagramm hat dort eine Lücke — eine kleinere Summe sähe aus wie ein
 * schwacher Ertrag.
 */
import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  MetricCard,
  SegmentedControl,
  icons,
} from "@hausmeter/ui";
import { Chart, type ChartAnnotation, type ChartSeries } from "@hausmeter/ui/chart";
import type { ApiComponents } from "@hausmeter/sdk";
import { Section } from "../components/section.js";
import { DefinitionList } from "../components/definition-list.js";
import { ENERGY_UNIT, formatEnergy, POWER_FORMAT, powerParts } from "../lib/energy-format.js";
import { formatTimeAgo } from "../lib/format.js";
import { RANGE_LABELS, rangeWindow } from "../lib/history-range.js";
import { deviceTypeInfo } from "../lib/device-type.js";
import { MAX_FADE } from "../lib/consumption-split.js";
import {
  assetStateGap,
  formatKw,
  formatKwh,
  storageRows,
} from "../lib/generation-asset-device.js";
import {
  useDevices,
  useGenerationAsset,
  useGenerationAssetHistory,
  useGenerationAssetState,
  useMeters,
} from "../lib/queries.js";
import {
  HISTORY_RANGES,
  navigate,
  routeFor,
  routePath,
  type HistoryRange,
  type Route,
} from "../lib/router.js";
import { DeleteButton, GenerationAssetDialog, kindLabel } from "./generation-assets.js";
import { meterAtPoint } from "../lib/generation-meters.js";
import { useSiteAccess } from "../lib/site-access.js";
import { StorageTenantPolicySection } from "../components/storage-tenant-policy.js";

type GenerationAsset = ApiComponents["schemas"]["GenerationAsset"];
type AssetState = ApiComponents["schemas"]["GenerationAssetState"];
type AssetHistory = ApiComponents["schemas"]["GenerationAssetHistoryResponse"];
type Device = ApiComponents["schemas"]["Device"];

const number = (value: number, digits = 1): string =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: digits }).format(value);
const percent = (ratio: number): string => `${Math.round(ratio * 100)}`;

export function GenerationAssetDetailScreen({
  siteId,
  assetId,
  range,
  siteTimeZone,
}: {
  siteId: string;
  assetId: string;
  range: HistoryRange;
  siteTimeZone: string;
}) {
  const asset = useGenerationAsset(assetId);

  if (asset.isPending) return <Card padding="sm">Wird geladen …</Card>;
  if (asset.isError || asset.data === undefined) {
    return (
      <Alert tone="danger">
        {asset.error?.message ?? "Die Erzeugungsanlage konnte nicht geladen werden."}
      </Alert>
    );
  }
  /* Eine Anlage eines anderen Hauses unter dieser Adresse — wie beim Gerät. */
  if (asset.data.siteId !== siteId) {
    return (
      <EmptyState title="Gehört zu einer anderen Anlage" icon={<icons.IconSun size={30} />}>
        Diese Erzeugungsanlage steht unter einer anderen Anlage. Wechsle oben die Anlage, um
        sie zu öffnen.
      </EmptyState>
    );
  }
  return (
    <AssetDetail siteId={siteId} asset={asset.data} range={range} siteTimeZone={siteTimeZone} />
  );
}

function AssetDetail({
  siteId,
  asset,
  range,
  siteTimeZone,
}: {
  siteId: string;
  asset: GenerationAsset;
  range: HistoryRange;
  siteTimeZone: string;
}) {
  const hasDevices = asset.deviceIds.length > 0;
  const state = useGenerationAssetState(hasDevices ? asset.id : null);
  const window = useMemo(() => rangeWindow(range), [range]);
  /* Auch ohne Gerät: Was frühere Geräte geliefert haben, bleibt der Anlage (HM-144). */
  const history = useGenerationAssetHistory(asset.id, window);
  const hasPast = (history.data?.devices.length ?? 0) > 0;
  const showHistory = hasDevices || hasPast;
  const allDevices = useDevices(siteId).data?.items ?? [];
  const devices = asset.deviceIds
    .map((id) => allDevices.find((device) => device.id === id))
    .filter((device): device is Device => device !== undefined)
    /* Nach Namen, nicht nach Kennung — „links" vor „rechts", in Liste und Legende gleich. */
    .sort((a, b) => a.name.localeCompare(b.name, "de"));
  const [editing, setEditing] = useState(false);
  // Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10).
  const rights = useSiteAccess();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26, maxWidth: 1100 }}>
      {!hasDevices && (
        <Alert
          tone="info"
          action={
            rights.canWrite ? (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                Gerät zuordnen …
              </Button>
            ) : undefined
          }
        >
          {hasPast
            ? "Diese Anlage hat heute kein Gerät. Der Verlauf zeigt, was ihre früheren Geräte geliefert haben."
            : "Diese Anlage hat noch kein Gerät. Mit dem Wechselrichter, Laderegler oder BMS zeigt sie hier, was sie leistet — und ihren Verlauf."}
        </Alert>
      )}
      {showHistory && (
        <Tiles asset={asset} state={state.data} history={history.data} range={range} />
      )}

      {showHistory && (
        <Section
          title="Verlauf"
          subtitle={
            asset.kind === "pv"
              ? "Was die Geräte dieser Anlage erzeugt haben, je Gerät übereinander."
              : "Was in den Speicher geht (+) und herauskommt (−), je Gerät."
          }
          action={
            <SegmentedControl
              size="sm"
              label="Zeitraum"
              value={range}
              onChange={(next) =>
                navigate({ kind: "generation-asset", siteId, assetId: asset.id, range: next })
              }
              options={HISTORY_RANGES.map((id) => ({ value: id, label: RANGE_LABELS[id] }))}
            />
          }
        >
          <Card>
            {history.isPending ? (
              <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Wird geladen …</span>
            ) : history.isError || history.data === undefined ? (
              <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                {history.error?.message ?? "Der Verlauf ist nicht abrufbar."}
              </span>
            ) : (
              <AssetChart
                asset={asset}
                history={history.data}
                devices={devices}
                timeZone={siteTimeZone}
              />
            )}
          </Card>
        </Section>
      )}

      <DevicesSection siteId={siteId} asset={asset} devices={devices} state={state.data} />
      <MasterData asset={asset} onEdit={rights.canWrite ? () => setEditing(true) : undefined} />
      <MeterSection
        siteId={siteId}
        asset={asset}
        onEdit={rights.canWrite ? () => setEditing(true) : undefined}
      />
      {/* Was der Speicher den Wohnungen zugesteht (HM-172) — nur an einem Speicher. */}
      {asset.kind === "battery" && <StorageTenantPolicySection asset={asset} canWrite={rights.canWrite} />}

      {rights.canDelete && (
        <Section title="Erzeugungsanlage löschen">
          <Card>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "var(--sp-3)",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "var(--sp-3)",
                border: "1px solid var(--hm-danger)",
                borderRadius: "var(--radius-md)",
              }}
            >
              <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 260px" }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--hm-danger)" }}>
                  {asset.label} löschen
                </span>
                <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
                  Gelöscht werden die Angaben dieser Anlage und ihre Zuordnungen. Geräte, Zähler und
                  ihre <strong>Verbrauchsdaten bleiben unberührt</strong> — an einer Erzeugungsanlage hängt
                  kein Wert.
                </span>
              </span>
              <span style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
                <DeleteButton
                  asset={asset}
                  label="Löschen …"
                  onDeleted={() => navigate(routeFor("generation", siteId), { replace: true })}
                />
              </span>
            </div>
          </Card>
        </Section>
      )}

      {rights.canWrite && editing && (
        <GenerationAssetDialog siteId={siteId} asset={asset} onClose={() => setEditing(false)} />
      )}
    </div>
  );
}

/**
 * Die Kacheln. **Eine Kachel ohne Wert entsteht gar nicht** — dieselbe Regel
 * wie am Gerät; eine Reihe aus „–" sähe aus wie ein Schaden und ist an einer
 * frisch zugeordneten Anlage der Normalfall.
 */
function Tiles({
  asset,
  state,
  history,
  range,
}: {
  asset: GenerationAsset;
  state: AssetState | undefined;
  history: AssetHistory | undefined;
  range: HistoryRange;
}) {
  const window = RANGE_LABELS[range];
  /* Fehlte einem Gerät der Stand vor dem Zeitraum, zählt der Zuwachs erst ab seinem ersten Wert (HM-132). */
  const partial = history?.energyPartial === true ? "ab dem ersten Wert im Zeitraum" : undefined;
  const tiles: { key: string; label: string; value: string; unit: string; caption?: string }[] =
    [];

  if (asset.kind === "pv") {
    const pv = state?.pv;
    if (pv?.generationW != null) {
      tiles.push({
        key: "power",
        label: "Leistung jetzt",
        value: powerParts(pv.generationW).text,
        unit: powerParts(pv.generationW).unit,
        ...(state?.measuredAt ? { caption: `gemeldet ${formatTimeAgo(state.measuredAt)}` } : {}),
      });
    }
    if (pv?.utilisation != null) {
      tiles.push({
        key: "utilisation",
        label: "Auslastung",
        value: percent(pv.utilisation),
        unit: "%",
        caption: "der Nennleistung",
      });
    }
    if (history?.energyInWh != null) {
      tiles.push({
        key: "yield",
        label: `Ertrag · ${window}`,
        value: formatEnergy(history.energyInWh),
        unit: ENERGY_UNIT,
        ...(partial ? { caption: partial } : {}),
      });
    }
    if (history?.specificYieldKwhPerKwp != null) {
      tiles.push({
        key: "specific",
        label: `Ertrag je kWp · ${window}`,
        value: number(history.specificYieldKwhPerKwp, 2),
        unit: "kWh/kWp",
        ...(partial ? { caption: partial } : {}),
      });
    }
  } else {
    const battery = state?.battery;
    if (battery?.soc != null) {
      tiles.push({ key: "soc", label: "Ladezustand", value: percent(battery.soc), unit: "%" });
    } else if (state !== undefined) {
      /* Mehrere Batterien: je Gerät, nicht gemittelt (HM-130). */
      const socs = state.devices.flatMap((device) =>
        device.battery?.soc.value == null ? [] : [percent(device.battery.soc.value)],
      );
      if (socs.length > 0) {
        tiles.push({ key: "soc", label: "Ladezustand je Batterie", value: socs.join(" / "), unit: "%" });
      }
    }
    if (battery?.powerW != null) {
      tiles.push({
        key: "power",
        label: "Leistung jetzt",
        value: powerParts(Math.abs(battery.powerW)).text,
        unit: powerParts(Math.abs(battery.powerW)).unit,
        caption: battery.powerW > 0 ? "lädt" : battery.powerW < 0 ? "entlädt" : "ruht",
      });
    }
    if (history?.energyInWh != null) {
      tiles.push({
        key: "charged",
        label: `Geladen · ${window}`,
        value: formatEnergy(history.energyInWh),
        unit: ENERGY_UNIT,
        ...(partial ? { caption: partial } : {}),
      });
    }
    if (history?.energyOutWh != null) {
      tiles.push({
        key: "discharged",
        label: `Entladen · ${window}`,
        value: formatEnergy(history.energyOutWh),
        unit: ENERGY_UNIT,
        ...(partial ? { caption: partial } : {}),
      });
    }
  }

  if (tiles.length === 0) {
    const gap = state === undefined ? null : assetStateGap(state);
    return (
      <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
        {gap ?? "Noch kein Wert gemeldet."}
      </p>
    );
  }
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
        gap: "var(--sp-4)",
      }}
    >
      {tiles.map((tile) => (
        <MetricCard
          key={tile.key}
          label={tile.label}
          value={tile.value}
          unit={tile.unit}
          caption={tile.caption}
          commodity="electricity"
          quantity={asset.kind === "pv" ? "generation" : undefined}
          icon={asset.kind === "pv" ? <icons.IconSun size={16} /> : <icons.IconBolt size={16} />}
        />
      ))}
    </div>
  );
}

/**
 * Der Verlauf. **Bei PV gestapelt je Gerät**, als Helligkeitsreihe der Größe
 * Erzeugung (`fade`, wie die Wohnungen im Mieterstrom, ADR 0013 Nachtrag) —
 * eine eigene Farbe je Laderegler wäre eine Aussage, die niemand gemeint hat.
 * Die Höhe des Stapels ist die Summe; fehlt einem Gerät ein Abschnitt, bricht
 * er dort ab. **Beim Speicher** eine Linie je Gerät mit Vorzeichen, denn eine
 * Ladung über einer Entladung zu stapeln ergäbe keine Größe.
 */
function AssetChart({
  asset,
  history,
  devices,
  timeZone,
}: {
  asset: GenerationAsset;
  history: AssetHistory;
  devices: readonly Device[];
  timeZone: string;
}) {
  /* Der Name kommt aus dem Verlauf: Ein freigegebenes Gerät steht in keiner Geräteliste mehr (HM-144). */
  const nameOf = (deviceId: string): string => {
    const entry = history.devices.find((device) => device.deviceId === deviceId);
    const name = devices.find((device) => device.id === deviceId)?.name ?? entry?.name ?? "Gerät";
    return entry?.assigned === false ? `${name} (früher)` : name;
  };
  /*
   * Ein früheres Gerät ist nach seinem Ende kein Summand mehr — gestapelt
   * stünde die Anlage ab dort als Lücke da. Mit einem früheren Gerät stehen
   * die Geräte deshalb nebeneinander statt übereinander.
   */
  const changed = history.devices.some((device) => !device.assigned);
  const last = Math.max(history.devices.length - 1, 1);
  const order = (deviceId: string): number => {
    const index = devices.findIndex((device) => device.id === deviceId);
    return index < 0 ? devices.length : index;
  };
  const ordered = [...history.devices].sort((a, b) => order(a.deviceId) - order(b.deviceId));
  const series: ChartSeries[] = ordered.map((device, index) => ({
    id: device.deviceId,
    label: nameOf(device.deviceId),
    mark: asset.kind === "pv" ? "area" : "line",
    quantity: asset.kind === "pv" ? "generation" : "storage_charge",
    fade: history.devices.length === 1 ? 0 : (index / last) * MAX_FADE,
    points: device.points.map((point) => ({ at: new Date(point.t).getTime(), value: point.v })),
  }));

  const from = new Date(history.from).getTime();
  const retained = history.retainedFrom === null ? null : new Date(history.retainedFrom).getTime();
  const annotations: ChartAnnotation[] =
    retained !== null && retained > from
      ? [{ at: from, until: retained, label: "nicht mehr aufbewahrt" }]
      : [];

  return (
    <Chart
      series={series}
      from={from}
      to={new Date(history.to).getTime()}
      bucket={history.resolution}
      format={POWER_FORMAT}
      timeZone={timeZone}
      height={230}
      stacked={asset.kind === "pv" && series.length > 1 && !changed}
      stackTotalLabel="Anlage"
      annotations={annotations}
      legend={series.length > 1}
      emptyMessage="In diesem Zeitraum hat kein Gerät dieser Anlage eine Leistung gemeldet."
      label={`Verlauf von ${asset.label}`}
    />
  );
}

/** Die Geräte der Anlage — je eines mit dem Teil, den es für sie meldet. */
function DevicesSection({
  siteId,
  asset,
  devices,
  state,
}: {
  siteId: string;
  asset: GenerationAsset;
  devices: readonly Device[];
  state: AssetState | undefined;
}) {
  if (devices.length === 0) return null;
  return (
    <Section
      title="Geräte"
      subtitle={
        asset.kind === "pv"
          ? "Die Wechselrichter und Laderegler dieser Anlage — je Gerät der PV-Eingang."
          : "Die Batterien und Wechselrichter dieses Speichers — je Gerät die Batterie."
      }
    >
      <Card padding="none">
        {devices.map((device, index) => {
          const part = state?.devices.find((entry) => entry.deviceId === device.id);
          const route: Route = { kind: "device", siteId, deviceId: device.id, tab: "overview" };
          const rows: (readonly [string, string])[] =
            part?.pv != null
              ? [
                  ...(part.pv.generationW === null
                    ? []
                    : ([["Leistung", formatKw(part.pv.generationW)]] as const)),
                  ...(part.pv.energyWh.value === null
                    ? []
                    : ([["Ertrag gesamt", formatKwh(part.pv.energyWh.value)]] as const)),
                ]
              : part?.battery != null
                ? storageRows(part.battery)
                : [];
          return (
            <div
              key={device.id}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "var(--sp-3)",
                alignItems: "flex-start",
                padding: "13px var(--sp-5)",
                borderBottom:
                  index < devices.length - 1 ? "1px solid var(--border-subtle)" : "none",
              }}
            >
              <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 220px", minWidth: 0 }}>
                <a
                  href={routePath(route)}
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)
                      return;
                    event.preventDefault();
                    navigate(route);
                  }}
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--text-strong)",
                    textDecoration: "none",
                    overflowWrap: "anywhere",
                  }}
                >
                  {device.name}
                </a>
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {deviceTypeInfo(device.deviceType).model}
                  {part?.measuredAt ? ` · gemeldet ${formatTimeAgo(part.measuredAt)}` : ""}
                </span>
              </span>
              <span style={{ flex: "1 1 260px", minWidth: 0 }}>
                {part?.reason === "part_not_reported" ? (
                  <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                    Meldet {asset.kind === "pv" ? "keinen PV-Eingang" : "keine Batterie"}.
                  </span>
                ) : part?.reason === "device_not_for_kind" ? (
                  <Badge tone="warning">passt nicht zur Art</Badge>
                ) : rows.length === 0 ? (
                  <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                    Noch kein Wert gemeldet.
                  </span>
                ) : (
                  <DefinitionList rows={rows} />
                )}
              </span>
            </div>
          );
        })}
      </Card>
    </Section>
  );
}

/** Die Stammdaten — was für eine Anlage das ist; geändert im selben Dialog wie in der Liste. */
function MasterData({
  asset,
  onEdit,
}: {
  asset: GenerationAsset;
  /** Fehlt, wenn die Teilung kein Ändern erlaubt (HM-160). */
  onEdit: (() => void) | undefined;
}) {
  const rows: (readonly [string, string])[] = [["Art", kindLabel(asset.kind)]];
  const kw = (value: number | null, unit: string) =>
    value === null ? null : `${number(value, 2)} ${unit}`;
  const push = (label: string, value: string | null) => {
    if (value !== null) rows.push([label, value]);
  };
  if (asset.kind === "pv") {
    push("Nennleistung", kw(asset.peakPowerKwp, "kWp"));
    push("Wechselrichter", kw(asset.inverterPowerKw, "kW"));
    push("Ausrichtung", asset.azimuthDeg === null ? null : `${number(asset.azimuthDeg, 0)}°`);
    push("Neigung", asset.tiltDeg === null ? null : `${number(asset.tiltDeg, 0)}°`);
  } else {
    push("Nennkapazität", kw(asset.capacityKwh, "kWh"));
    push("Nutzbare Kapazität", kw(asset.usableCapacityKwh, "kWh"));
    push("Ladeleistung", kw(asset.maxChargePowerKw, "kW"));
    push("Entladeleistung", kw(asset.maxDischargePowerKw, "kW"));
  }
  push("Inbetriebnahme", asset.commissionedOn);
  return (
    <Section
      title="Stammdaten"
      action={
        onEdit !== undefined ? (
          <Button variant="secondary" size="sm" onClick={onEdit}>
            Ändern …
          </Button>
        ) : undefined
      }
    >
      <Card>
        <DefinitionList rows={rows} />
      </Card>
    </Section>
  );
}

/**
 * Wie die Anlage in die Bilanz eingeht: über den Zähler, hinter dem sie steht.
 * **Geändert wird das im Dialog der Anlage oder im Messkonzept** (#42) — beide
 * schreiben dasselbe Feld.
 */
function MeterSection({
  siteId,
  asset,
  onEdit,
}: {
  siteId: string;
  asset: GenerationAsset;
  /** Fehlt, wenn die Teilung kein Ändern erlaubt (HM-160). */
  onEdit: (() => void) | undefined;
}) {
  const meters = useMeters(siteId, true).data?.items ?? [];
  const meter = asset.meteringPointId === null ? null : meterAtPoint(meters, asset.meteringPointId);
  const conceptRoute = routeFor("concept", siteId);
  const meterRoute: Route | null =
    meter === null
      ? null
      : { kind: "meter", siteId, meterId: meter.id, tab: "overview", register: null, range: "24h" };

  return (
    <Section title="Zähler">
      <Card>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-3)",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span style={{ fontSize: 13, lineHeight: 1.55, color: "var(--text-body)", flex: "1 1 280px" }}>
            {meterRoute === null ? (
              <>
                <Badge tone="neutral">nicht gemessen</Badge>{" "}
                Diese Anlage steht hinter keinem Zähler und geht deshalb in keine Bilanz ein.
              </>
            ) : (
              <>
                Steht hinter{" "}
                <a
                  href={routePath(meterRoute)}
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)
                      return;
                    event.preventDefault();
                    navigate(meterRoute);
                  }}
                  style={{ color: "var(--text-strong)", fontWeight: 600, textDecoration: "none" }}
                >
                  {meter!.label}
                </a>
                . Über diesen Zähler geht sie in die Bilanz unter „Strom &amp; Erzeugung" ein.
              </>
            )}
          </span>
          <span style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
            {onEdit !== undefined && (
              <Button variant="secondary" size="sm" onClick={onEdit}>
                Zähler ändern …
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate(conceptRoute)}>
              Zum Messkonzept
            </Button>
          </span>
        </div>
      </Card>
    </Section>
  );
}
